import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, money } from "../../cart";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("emporium_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([
        axios.get(`${API}/admin/orders`, auth),
        axios.get(`${API}/admin/products`, auth),
      ]);
      setOrders(o.data); setProducts(p.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { localStorage.removeItem("emporium_admin_token"); navigate("/admin/login"); }
    }
  }, []);

  useEffect(() => { if (!token) { navigate("/admin/login"); return; } load(); }, []);

  const saveProduct = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, {
        name: p.name, category: p.category, description: p.description, price: Number(p.price),
        sku: p.sku, image: p.image, images: p.images || [], stock: Number(p.stock), active: p.active, featured: p.featured,
      }, auth);
      toast.success("Product saved"); load();
    } catch { toast.error("Save failed"); }
  };
  const refund = async (id) => { if (!window.confirm("Issue a full refund for this order?")) return; try { await axios.post(`${API}/admin/orders/${id}/refund`, {}, auth); toast.success("Refund issued"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); } };
  const setFulfil = async (id, status) => { try { await axios.patch(`${API}/admin/orders/${id}`, { fulfillment_status: status }, auth); load(); } catch { toast.error("Update failed"); } };

  const setP = (id, k, v) => setProducts((prev) => prev.map((p) => p.id === id ? { ...p, [k]: v } : p));

  return (
    <main className="min-h-screen bg-[var(--bg-primary)] px-6 md:px-10 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img src="/emporium-logo.png" alt="Emporium London" className="h-8" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Admin</span>
        </div>
        <button data-testid="admin-logout" onClick={() => { localStorage.removeItem("emporium_admin_token"); navigate("/admin/login"); }} className="font-mono text-[10px] tracking-[0.2em] uppercase border border-black/20 px-4 h-9 hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors">Sign out</button>
      </div>

      <div className="flex gap-2 mb-8">
        {["orders", "products"].map((t) => (
          <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-5 h-10 border ${tab === t ? "bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]" : "border-black/20 text-[var(--text-secondary)]"}`}>{t}</button>
        ))}
      </div>

      {tab === "orders" && (
        <div data-testid="admin-orders" className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="text-left font-mono text-[10px] tracking-[0.15em] uppercase text-[var(--text-muted)] border-b border-black/10">
              <th className="py-3 pr-4">Order</th><th className="pr-4">Customer</th><th className="pr-4">Total</th><th className="pr-4">Payment</th><th className="pr-4">Fulfilment</th><th>Actions</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-black/5 align-top">
                  <td className="py-4 pr-4 font-mono text-xs">{o.order_number}<br /><span className="text-[var(--text-muted)]">{new Date(o.created_at).toLocaleDateString()}</span></td>
                  <td className="pr-4">{o.customer?.name}<br /><span className="text-[var(--text-muted)] text-xs">{o.customer?.email}</span><br /><span className="text-[var(--text-muted)] text-xs">{(o.customer?.delivery_address || o.customer?.billing_address || "").slice(0, 40)}</span></td>
                  <td className="pr-4">{money(o.total)}</td>
                  <td className="pr-4"><span className={`font-mono text-[10px] uppercase ${o.payment_status === "paid" ? "text-green-700" : o.payment_status === "refunded" ? "text-amber-700" : "text-[var(--text-muted)]"}`}>{o.payment_status}</span></td>
                  <td className="pr-4">
                    <select value={o.fulfillment_status || "unfulfilled"} onChange={(e) => setFulfil(o.id, e.target.value)} className="bg-transparent border border-black/20 text-xs py-1 px-2">
                      {["unfulfilled", "processing", "shipped", "delivered"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>{o.payment_status === "paid" && <button onClick={() => refund(o.id)} className="font-mono text-[10px] uppercase border border-black/20 px-3 h-8 hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)]">Refund</button>}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-[var(--text-muted)] font-mono text-xs uppercase">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "products" && (
        <div data-testid="admin-products" className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="text-left font-mono text-[10px] tracking-[0.15em] uppercase text-[var(--text-muted)] border-b border-black/10">
              <th className="py-3 pr-4">Product</th><th className="pr-4">SKU</th><th className="pr-4">Price £</th><th className="pr-4">Stock</th><th className="pr-4">Active</th><th></th></tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-black/5">
                  <td className="py-3 pr-4 max-w-xs">{p.name}</td>
                  <td className="pr-4 font-mono text-xs">{p.sku}</td>
                  <td className="pr-4"><input type="number" step="0.01" value={p.price} onChange={(e) => setP(p.id, "price", e.target.value)} className="w-20 bg-transparent border border-black/20 px-2 py-1" /></td>
                  <td className="pr-4"><input data-testid={`admin-stock-${p.sku}`} type="number" value={p.stock} onChange={(e) => setP(p.id, "stock", e.target.value)} className="w-20 bg-transparent border border-black/20 px-2 py-1" /></td>
                  <td className="pr-4"><input type="checkbox" checked={p.active} onChange={(e) => setP(p.id, "active", e.target.checked)} /></td>
                  <td><button data-testid={`admin-save-${p.sku}`} onClick={() => saveProduct(p)} className="font-mono text-[10px] uppercase border border-black/20 px-3 h-8 hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)]">Save</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-6 font-mono text-[10px] text-[var(--text-muted)] uppercase">Tip: set the chess set stock here once fulfilment is confirmed to make it buyable.</p>
        </div>
      )}
    </main>
  );
}
